import React from 'react';
import { Button } from 'antd';
import { motion } from 'framer-motion';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translate } from '../helpers/translationHelper';
import { TRANSLATIONS } from '../constants/translations';

const WARM_ACCENT = '#E2A28F';
const WARM_ACCENT_LIGHT = '#EAB9A0';

/**
 * Shown above "My spaces" whenever Stripe has revoked a host's payout
 * verification and StripeController's webhook auto-unpublished their
 * listing(s) as a result (see AuthController's own PublishSpace check for
 * the flip side -- the same condition also blocks *publishing* a new one).
 * Same warm/coral palette as the email's Warning theme (EmailLayout.cs),
 * kept as its own banner rather than a plain antd Alert to match the
 * app's rounded, animated visual language.
 */
const PayoutIssueBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16,
        marginBottom: 20,
        padding: '16px 20px',
        borderRadius: 20,
        background: `linear-gradient(135deg, ${WARM_ACCENT}14, ${WARM_ACCENT_LIGHT}14)`,
        border: `1px solid ${WARM_ACCENT}33`,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${WARM_ACCENT}, ${WARM_ACCENT_LIGHT})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconAlertTriangle size={22} color="#FFFFFF" stroke={1.8} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PAYOUTS_BANNER_TITLE)}</div>
        <div style={{ marginTop: 2, color: '#667085', fontSize: 13, lineHeight: 1.5 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PAYOUTS_BANNER_DESCRIPTION)}
        </div>
      </div>

      <Button
        shape="round"
        style={{ flexShrink: 0, borderColor: WARM_ACCENT, color: WARM_ACCENT }}
        onClick={() => navigate('/settings?section=payments')}
      >
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PAYOUTS_BANNER_CTA)}
      </Button>
    </motion.div>
  );
};

export default PayoutIssueBanner;
